/**
 * Backend Permission Enforcement Utility
 * 
 * This module provides server-side permission checking that cannot be bypassed
 * by frontend manipulation. All sensitive mutations must use these functions
 * to ensure proper RBAC enforcement.
 */

import { UserRole } from '../types/user';
import { Permission, hasPermissionInOrganization } from '../features/shared/permissions';
import { OrganizationType } from '../models/Organization';
import { UserService } from '../services/UserService';
import { OrganizationService } from '../services/OrganizationService';
import { logger } from './logger';
import { logPermissionDenied } from './auditLogger';

/**
 * Permission enforcement context
 * Contains the information needed to check permissions
 */
export interface PermissionContext {
  userId: string;
  userRole: UserRole;
  orgId: string;
  organizationType: OrganizationType;
}

/**
 * Require a specific permission for the current user
 * Throws an error if the user does not have the permission
 * 
 * @param context - User and organization context
 * @param permission - Required permission
 * @param actionDescription - Human-readable description of the action (for error messages)
 * @throws Error if permission is missing
 */
export function requirePermission(
  context: PermissionContext,
  permission: Permission,
  actionDescription?: string
): void {
  const { userRole, organizationType } = context;
  
  // Admin always has all permissions
  if (userRole === 'admin') {
    return;
  }
  
  // Check if user has the required permission
  if (!hasPermissionInOrganization(userRole, organizationType, permission)) {
    const action = actionDescription || 'perform this action';
    
    // Log permission denial (non-blocking)
    logPermissionDenied(
      context.orgId,
      context.userId,
      context.userRole,
      permission,
      action,
      { organizationType: context.organizationType }
    ).catch(err => {
      // Silently fail - audit logging should never break permission checks
      logger.error('[PermissionEnforcement] Failed to log permission denial:', err);
    });
    
    throw new Error(
      `You do not have permission to ${action}. ` +
      `Required permission: ${permission}`
    );
  }
}

/**
 * Check if a user has a specific permission
 * Returns boolean instead of throwing
 * 
 * @param context - User and organization context
 * @param permission - Permission to check
 * @returns true if user has permission, false otherwise
 */
export function hasPermission(
  context: PermissionContext,
  permission: Permission
): boolean {
  const { userRole, organizationType } = context;
  
  // Admin always has all permissions
  if (userRole === 'admin') {
    return true;
  }
  
  return hasPermissionInOrganization(userRole, organizationType, permission);
}

/**
 * Require any of the specified permissions
 * Throws if user has none of the required permissions
 * 
 * @param context - User and organization context
 * @param permissions - Array of permissions (user needs at least one)
 * @param actionDescription - Human-readable description of the action
 * @throws Error if no permissions are present
 */
export function requireAnyPermission(
  context: PermissionContext,
  permissions: Permission[],
  actionDescription?: string
): void {
  const { userRole, organizationType } = context;
  
  // Admin always has all permissions
  if (userRole === 'admin') {
    return;
  }
  
  // Check if user has at least one of the required permissions
  const hasAny = permissions.some(permission =>
    hasPermissionInOrganization(userRole, organizationType, permission)
  );
  
  if (!hasAny) {
    const action = actionDescription || 'perform this action';
    const permissionList = permissions.join(', ');
    throw new Error(
      `You do not have permission to ${action}. ` +
      `Required one of: ${permissionList}`
    );
  }
}

/**
 * Require all of the specified permissions
 * Throws if user is missing any of the required permissions
 * 
 * @param context - User and organization context
 * @param permissions - Array of permissions (user needs all)
 * @param actionDescription - Human-readable description of the action
 * @throws Error if any permission is missing
 */
export function requireAllPermissions(
  context: PermissionContext,
  permissions: Permission[],
  actionDescription?: string
): void {
  const { userRole, organizationType } = context;
  
  // Admin always has all permissions
  if (userRole === 'admin') {
    return;
  }
  
  // Check if user has all required permissions
  const missingPermissions = permissions.filter(permission =>
    !hasPermissionInOrganization(userRole, organizationType, permission)
  );
  
  if (missingPermissions.length > 0) {
    const action = actionDescription || 'perform this action';
    const missingList = missingPermissions.join(', ');
    throw new Error(
      `You do not have permission to ${action}. ` +
      `Missing permissions: ${missingList}`
    );
  }
}

/**
 * Fallback permission context when fetch fails or user/org missing.
 * Minimal-permission (student + club) so privileged actions are denied; avoids hard throws.
 */
function getFallbackPermissionContext(userId: string, orgId: string): PermissionContext {
  const fallback: PermissionContext = {
    userId,
    userRole: 'student',
    orgId,
    organizationType: 'club',
  };
  return fallback;
}

/**
 * Fetch permission context from database
 * Helper function to get user role and organization type for permission checking
 * Never throws: returns fallback context if user/org missing or fetch fails.
 * 
 * @param userId - User ID
 * @param organizationId - Organization ID
 * @returns Permission context (or fallback)
 */
export async function fetchPermissionContext(
  userId: string,
  orgId: string
): Promise<PermissionContext> {
  try {
    // Fetch user and organization in parallel
    const [user, organization] = await Promise.all([
      UserService.getUserById(userId),
      OrganizationService.getOrganization(orgId)
    ]);

    if (!user || !organization) {
      logger.warn('[PermissionEnforcement] User or organization not found, using fallback context', {
        userId,
        orgId,
        hasUser: !!user,
        hasOrganization: !!organization,
      });
      return getFallbackPermissionContext(userId, orgId);
    }

    const context: PermissionContext = {
      userId: user.uid,
      userRole: user.role,
      orgId: organization.id,
      organizationType: organization.type,
    };

    // Validate org relationship on user profile
    const userOrgId = (user as any).orgId;
    if (!userOrgId) {
      logger.warn('[PermissionEnforcement] User is missing orgId in profile', {
        userId: user.uid,
      });
    } else if (userOrgId !== organization.id) {
      logger.warn('[PermissionEnforcement] User orgId does not match organization', {
        userId: user.uid,
        userOrgId,
        orgId: organization.id,
      });
    }

    return context;
  } catch (error) {
    // Log with full details but do NOT throw – permission context must never crash the app
    logger.error('Error fetching permission context:', {
      error,
      userId,
      orgId,
    });

    return getFallbackPermissionContext(userId, orgId);
  }
}

